import { filterNonUniqueByProperty } from "@/app/apiutils";
import { containerService } from "@/app/services/extensions/containerServiceExtended";
import { keys, translate, translateWithParams } from "@/plugins/i18n";
import { computed } from "vue";
import type { ContainerHistory } from "@/app/services/generated/containerService";
import { ContainerFilter } from "@/app/services/generated/containerService";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import type GridConfigurationBuilder from "@/components/controls/grid/builder/GridConfigurationBuilder";

export interface ScannedContainerItem {
  permLabel: string;
  scannedThisSession: boolean; // If the container belongs to the current session
  errorMessage: string | null;
  hasBeenValidated: boolean;
  mostRecentHistory?: ContainerHistory;
  palletLabelAssociated: string | null;
}

export interface ScannedContainersEvent {
  toAdd: ScannedContainerItem[];
  toRemove: string[];
  noteToAppend?: string;
}

function getScanningErrorMessage(scannedContainers: ScannedContainerItem[]) {
  if (scannedContainers.length === 0) {
    return null;
  }
  if (scannedContainers.some((x) => x.errorMessage)) {
    return translate(keys.containers_fill_error).toString();
  }
  return null;
}

export async function validateScannedContainers(
  itemsToValidate: ScannedContainerItem[],
  scannedContainers: ScannedContainerItem[],
  opts?: {
    checkLatestHistories?: boolean;
    additionalContainerValidation?: (container: ScannedContainerItem) => string;
  }
) {
  const toScanLabels = itemsToValidate;

  if (toScanLabels.length === 0) {
    return true;
  }

  toScanLabels.forEach((x) => {
    x.hasBeenValidated = false;
    x.errorMessage = null;
  });

  const containers = await containerService.getContainersByPermLabels(
    toScanLabels.map((x) => x.permLabel)
  );

  const permLabelToLatestHistoryMap: Map<string, ContainerHistory> = new Map();

  if (opts?.checkLatestHistories) {
    const containerFilter = new ContainerFilter();
    containerFilter.containerIds = containers.map((container) => container.id);

    const summaries =
      await containerService.getContainerSummaries(containerFilter);

    summaries.containers.forEach(({ container, latestHistory }) => {
      permLabelToLatestHistoryMap.set(container.permLabel, latestHistory);
    });
  }

  // check all permLabels are valid
  toScanLabels.forEach((x) => {
    if (opts?.checkLatestHistories) {
      x.mostRecentHistory = permLabelToLatestHistoryMap.get(x.permLabel);
    }

    if (!containers.some((c) => c.permLabel === x.permLabel)) {
      x.errorMessage = translate(keys.containers_not_found).toString();
    } else if (opts?.additionalContainerValidation) {
      x.errorMessage = opts.additionalContainerValidation(x) || null;
    }
    x.hasBeenValidated = true;
  });

  return getScanningErrorMessage(scannedContainers) === null;
}

export function getScannedContainersText(totalSessionScannedPermLabel: number) {
  return translateWithParams("containers_scanned_this_session_summary", {
    scannedThisSession: totalSessionScannedPermLabel.toString(),
  });
}

export const useScanContainers = (opts?: {
  additionalContainerValidation?: (container: ScannedContainerItem) => string;
}) => {
  const scannedContainers = makeDeepRef<ScannedContainerItem[]>([]);
  const addContainer = async (label: string, palletLabel?: string) => {
    const labelToAdd: ScannedContainerItem = {
      permLabel: label,
      scannedThisSession: true,
      errorMessage: null,
      hasBeenValidated: false,
      palletLabelAssociated: palletLabel ?? null,
    };
    await validateScanning(labelToAdd, true);
    scannedContainers.value.unshift(labelToAdd);
    scannedContainers.value = filterNonUniqueByProperty(
      scannedContainers.value,
      (x) => x.permLabel
    );
  };

  const removeContainer = (permLabel: string) => {
    scannedContainers.value = scannedContainers.value.filter(
      (x) => x.permLabel !== permLabel
    );
  };

  const validateScanning = async (
    containerItem?: ScannedContainerItem,
    checkLatestHistories?: boolean
  ) => {
    const toScanLabels = containerItem
      ? [containerItem]
      : scannedContainers.value.filter((x) => x.scannedThisSession);
    return validateScannedContainers(toScanLabels, scannedContainers.value, {
      checkLatestHistories,
      additionalContainerValidation: opts?.additionalContainerValidation,
    });
  };

  const scanningErrorMessage = computed(() =>
    getScanningErrorMessage(scannedContainers.value)
  );

  const currentSessionPermLabels = computed(() =>
    scannedContainers.value.filter((x) => x.scannedThisSession)
  );

  const scannedContainersText = computed(() =>
    getScannedContainersText(currentSessionPermLabels.value.length)
  );

  const scannedContainersSummary = computed(() =>
    translateWithParams("containers_total_filled_summary", {
      totalScanned: `${scannedContainers.value.length}`,
    })
  );

  const getSessionScannedContainers = () => {
    return scannedContainers.value.filter((x) => x.scannedThisSession);
  };

  const resetUseScanContainersState = () => {
    scannedContainers.value.forEach((container) => {
      container.scannedThisSession = false;
    });
  };

  return {
    addContainer,
    removeContainer,
    validateScanning,
    scannedContainers,
    scanningErrorMessage,
    scannedContainersText,
    scannedContainersSummary,
    currentSessionPermLabels,
    resetUseScanContainersState,
    getSessionScannedContainers,
  };
};

export const getClassForRow = (item: ScannedContainerItem) => {
  if (item.errorMessage !== null) {
    return ["grid-row-error"];
  } else if (!item.scannedThisSession || item.hasBeenValidated) {
    return ["grid-row-success"];
  } else {
    return [];
  }
};

export class ScannedContainerColumns {
  private _builder: GridConfigurationBuilder<ScannedContainerItem>;

  constructor(builder: GridConfigurationBuilder<ScannedContainerItem>) {
    this._builder = builder;
  }

  public AddPermLabel() {
    this._builder.addTextColumn(
      keys.containers_perm_label,
      (item) => item.permLabel
    );
  }

  public AddErrorMessage() {
    this._builder
      .addTextColumn(keys.container_error_message, (item) => item.errorMessage)
      .withTextTruncation();
  }

  public AddPalletBarcode() {
    this._builder.addTextColumn(
      keys.pallet_barcode,
      (item) => item.palletLabelAssociated
    );
  }

  public AddAllStandardScannedContainerColumns() {
    this.AddPermLabel();
    this.AddErrorMessage();
    this.AddPalletBarcode();
  }
}
